import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Divider,
    InputAdornment,
    LinearProgress,
    Paper,
    Stack,
    Typography
} from "@mui/material";
import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from "react";
import {ApolloClient, NormalizedCacheObject, useLazyQuery, useMutation, useQuery} from "@apollo/client";
import {GET_PAYMENT_METHODS} from "../../../../store/queries/GetPaymentMethods";
import {UseQuerySuspense} from "../../../../components/UseQuerySuspense/UseQuerySuspense";
import {GET_ACTIVE_DEPOSIT_LIMITS} from "../../../../store/queries/GetActiveDepositLimits";
import CurrencyFormatter from "../../../../components/CurrencyFormatter/CurrencyFormatter";
import TextInput from "../../../../components/TextInput/TextInput";
import {AmountButton} from "./components/AmountButton";
import {PaymentMethodComponent} from "./components/PaymentMethod";
import {START_PAYMENT_MUTATION} from "../../../../store/mutations/StartPayment";
import {LoadingButton} from "@mui/lab";
import {useTranslation} from "react-i18next";
import {
    GetActiveDepositLimitsQuery,
    GetPaymentMethodsQuery,
    PaymentMethodInput,
    StartPaymentMutationMutation,
    StartPaymentMutationMutationVariables,
    PaymentMethod,
    GetDepositLimitsQuery,
    UserLimit,
    CancelQueuedDepositLimitMutationMutation,
    CancelQueuedDepositLimitMutationMutationVariables,
    UserLimitStatus,
    DeactivateActiveDepositLimitMutationMutation,
    DeactivateActiveDepositLimitMutationMutationVariables,
    UpdatePaymentMutationMutation, UpdatePaymentMutationMutationVariables, PaymentTxStatus, GetPaymentStatusQueryQuery
} from "../../../../gql/graphql";
import {css} from "@linaria/core";
import {OpenWithdrawals} from "./OpenWithdrawals";
import {CANCEL_QUEUED_DEPOSIT_LIMIT} from "../../../../store/mutations/CancelQueuedDepositLimit";
import {DEACTIVATE_DEPOSIT_LIMIT} from "../../../../store/mutations/DeactivateActiveDepositLimit";
import CloseIcon from "@mui/icons-material/Close";
import {BlurredBackdropModal} from "../../../../components/BlurredBackdropModal/BlurredBackdropModal";
import {GET_DEPOSIT_LIMITS} from "../../../../store/queries/GetDepositLimits";
import {QueryResult} from "@apollo/client/react/types/types";
import {useTimer} from "react-timer-hook";
import {CustomLink} from "../../../../components/CustomLink/CustomLink";
import {UpdatePayment} from "./components/UpdatePayment";
import {GET_USER} from "../../../../store/queries/GetUser";
import {GET_PAYMENT_STATUS} from "../../../../store/queries/GetPaymentStatus";
import {initializeApollo} from "../../../../store/StoreLink";

interface state {

    amount: number | "",
    selectedMethod: PaymentMethod | null
}

interface IFrameData {
    showIframe: boolean;
    iframeUri: string;
}

const DepositLimits = () => {
    const {t} = useTranslation();
    const depositLimits = useQuery<GetActiveDepositLimitsQuery>(GET_ACTIVE_DEPOSIT_LIMITS, {
        fetchPolicy: 'network-only'
    });
    const allDepositLimits = useQuery<GetDepositLimitsQuery>(GET_DEPOSIT_LIMITS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    });
    const [cancelActiveConfirmation, setCancelActiveConfirmation] = useState(false);
    const deactivateLimit = useMutation<DeactivateActiveDepositLimitMutationMutation, DeactivateActiveDepositLimitMutationMutationVariables>(DEACTIVATE_DEPOSIT_LIMIT);

    const cancelDepositLimit = () => {
        deactivateLimit[0]({
            variables: {
                limitId: parseInt(depositLimits.data?.limits?.nodes?.[0]?.id as string)
            },
            onCompleted(data) {
                depositLimits.refetch();
                allDepositLimits.refetch();
                setCancelActiveConfirmation(true);
            },
            onError(data) {

            }
        })
    }
    return (<UseQuerySuspense data={depositLimits}>
        {depositLimits.data?.limits?.nodes != null && depositLimits.data?.limits?.nodes?.length > 0 &&
            (<>{depositLimits.data?.limits?.nodes?.map((node, key) => {
                    return (<Box key={key}>
                        <Typography variant={'body2'} sx={{mb: 3}}>
                            {t('depositsLimitedToAmount', {
                                amount: CurrencyFormatter({
                                    currency: 'EUR',
                                    quantity: (node as UserLimit).value
                                })
                            })}
                        </Typography>
                        <Box sx={{marginBottom: 3}}>
                            <LinearProgress variant="determinate"
                                            value={(node as UserLimit).currentAmount ?? 0 / (node as UserLimit).value * 100}
                                            sx={(theme) => ({
                                                backgroundColor: '#fff',
                                                height: 11,
                                                border: '1px solid',
                                                borderColor: theme.palette.primary.main,
                                                borderRadius: 3
                                            })}/>
                            <Typography key={key} variant={'body2'}>
                                <CurrencyFormatter currency={'EUR'}
                                                   quantity={(node as UserLimit).currentAmount ?? 0}/>
                                &nbsp;
                                /
                                &nbsp;
                                <CurrencyFormatter currency={'EUR'}
                                                   quantity={(node as UserLimit).value}/>
                                {allDepositLimits.data?.limits?.nodes?.find((limit) => limit?.status === UserLimitStatus.Active) != null
                                    && allDepositLimits.data?.limits?.nodes?.find((limit) => limit?.status !== UserLimitStatus.Active && limit?.value == 2147483647) == null && (
                                        <Button onClick={cancelDepositLimit} sx={{ml: 2}}>
                                            {t('cancelLimit')}
                                        </Button>
                                    )}
                                <BlurredBackdropModal
                                    open={cancelActiveConfirmation}
                                    handleClose={() => setCancelActiveConfirmation(false)}
                                    dialogSx={{width: 450}}
                                >
                                    <>
                                        <Box sx={{
                                            display: 'block',
                                            width: '100%',
                                            position: 'relative'
                                        }}>

                                            <Button
                                                onClick={() => setCancelActiveConfirmation(false)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    pt: 2
                                                }}>
                                                <CloseIcon
                                                    sx={{
                                                        color: (theme) => theme.palette.text.primary,
                                                        width: 24,
                                                        height: 24
                                                    }}/>
                                            </Button>
                                        </Box>
                                        <Box sx={{
                                            paddingLeft: 4,
                                            paddingRight: 4,
                                            paddingBottom: 2,
                                            paddingTop: 4,
                                            overflowY: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}>
                                            <Typography variant={'h5'}
                                                        sx={{
                                                            pb: 2,
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            alignItems: 'center'
                                                        }}>
                                                {t('cancelLimit')}
                                            </Typography>

                                            <Typography variant={'body1'} sx={{pr: 2}}>
                                                {t('limitQueuedToBeCancelled')}
                                            </Typography>
                                            <Divider sx={{mx: -4, mt: 2}}/>
                                            <Stack direction={'row'} spacing={2} sx={{mt: 2, pr: 0}}
                                                   justifyContent={'flex-end'}>
                                                <Button variant={'contained'}
                                                        onClick={() => setCancelActiveConfirmation(false)}>{t('ok')}</Button>
                                            </Stack>
                                        </Box>
                                    </>
                                </BlurredBackdropModal>
                            </Typography>
                        </Box>
                    </Box>);
                })}
                </>

            )}
    </UseQuerySuspense>)
}

const QRAmount = (props: { amount: number }) => {

    const {t} = useTranslation();

    return (<Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant={'body1'}>{t('depositAmount')}:</Typography><Typography variant={'body1'}
                                                                                    sx={{fontWeight: 500}}>
        <CurrencyFormatter currency={'EUR'}
                           quantity={props.amount}/></Typography></Stack>)
}
const QRTimer = (props: {
    setIframeState: Dispatch<SetStateAction<IFrameData>>,
    paymentId: string,
    setConfirmationMessageData: Dispatch<SetStateAction<{ show: boolean, paymentTxId: string }>>
}) => {
    const timertime = 180;
    const time = new Date();
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    time.setSeconds(time.getSeconds() + timertime);
    const {
        seconds,
        minutes
    } = useTimer({
        expiryTimestamp: time, onExpire: () => {
            props.setIframeState({showIframe: false, iframeUri: ''});
            props.setConfirmationMessageData({paymentTxId: props.paymentId, show: true});
        }
    });
    const [getPaymentStatus, paymentStatus] = useLazyQuery<GetPaymentStatusQueryQuery>(GET_PAYMENT_STATUS, {
        variables: {
            paymentId: props.paymentId
        },
        onCompleted(data) {
            (window as any).dataLayer = (window as any).dataLayer || [];
            if (data?.paymentStatus?.paymentInformation?.status != PaymentTxStatus.Started && data?.paymentStatus?.paymentInformation?.status != PaymentTxStatus.InProcess) {
                let apolloClient = initializeApollo({});
                (apolloClient as ApolloClient<NormalizedCacheObject>).cache.writeQuery({
                    data: {identity: data?.paymentStatus?.user},
                    query: GET_USER
                });

                props.setIframeState({showIframe: false, iframeUri: ''});
                props.setConfirmationMessageData({paymentTxId: props.paymentId, show: true});
            }
        },
        onError(data) {
        },

        pollInterval: 1500,
        fetchPolicy: 'network-only'
    });

    const updatePayment = () => {
        if (paymentStatus.called)
            paymentStatus.refetch();
        else
            getPaymentStatus();
    }

    useEffect(() => {

        timerRef.current = setTimeout(updatePayment, 5000);
        return () => clearTimeout(timerRef.current as NodeJS.Timeout);

    }, []);

    return (<><LinearProgress variant="determinate"
                              value={(timertime - (minutes * 60 + seconds)) / timertime * 100}
                              sx={(theme) => ({
                                  backgroundColor: '#fff',
                                  height: 6,
                                  border: '1px solid',
                                  borderColor: theme.palette.primary.main,
                                  borderRadius: 1
                              })}/>
        <Typography variant={'body2'} sx={{
            textAlign: 'center',
            marginTop: '8px !important'
        }}>{`${minutes}:${seconds} min`}</Typography> </>);
}
export default (props: { showTitle?: boolean, forQr?: boolean }) => {
    const showTitle = props.showTitle ?? true;
    const forQr = props.forQr ?? false;
    const {t} = useTranslation();
    const paymentMethods = useQuery<GetPaymentMethodsQuery>(GET_PAYMENT_METHODS);

    const [startPayment, startPaymentState] = useMutation<StartPaymentMutationMutation, StartPaymentMutationMutationVariables>(START_PAYMENT_MUTATION);
    const [iframeData, setIframeData] = useState<IFrameData>({
        showIframe: false,
        iframeUri: ""
    })

    const [confirmationMessageData, setConfirmationMessageData] = useState<{ show: boolean, paymentTxId: string }>({
        show: false,
        paymentTxId: ""
    })
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [successMessages, setSuccessMessages] = useState<string[]>([]);


    const onStartPayment = () => {
        startPayment({
            variables: {
                input: {
                    paymentMethod: state.selectedMethod as PaymentMethodInput,
                    amount: state.amount
                },
            },
            onCompleted(data) {
                if (data.startPayment?.iframe) {
                    setIframeData({showIframe: true, iframeUri: data.startPayment?.forwardUrl as string});
                    setConfirmationMessageData({
                        ...confirmationMessageData,
                        paymentTxId: data.startPayment?.paymentTxId as string
                    });
                } else {
                    window.location.href = data.startPayment?.forwardUrl as string;
                }
                setErrorMessages([]);
                setSuccessMessages([]);
            },
            onError(data) {
                setSuccessMessages([]);
                try {
                    setErrorMessages([t(data.message)])
                } catch (e) {
                    setErrorMessages([t('unexpectedErrorTryAgainLater')])
                }
            }
        });
    }

    const [state, setState] = useState<state>({
        amount: 0,

        selectedMethod: null
    });

    const changeAmount = (value: number) => {
        setState({...state, amount: value})
    }

    const selectPaymenthMethod = (method: PaymentMethod) => {
        setState({...state, selectedMethod: method});
    }

    useEffect(() => {
        if (forQr)
            selectPaymenthMethod(paymentMethods.data?.paymentMethods?.filter((x) => (x as PaymentMethod).paymentProduct.name == 'Bancontact-QR').at(0) as PaymentMethod);
    }, [paymentMethods.data])
    return (<Box sx={{
        p: 0, maxWidth: 1, display: 'flex', justifyContent:'center'
    }}>
        <Stack spacing={2} direction={'column'} sx={{width:1}}>
            {showTitle && (<Typography variant={'h5'} sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: 64
            }}>{forQr ? t('QR-code') : t('deposit')}</Typography>)}
            {errorMessages.length > 0 && (
                <Box sx={{flexBasis: '100%', mb: 2, mt: 2}}>
                    {errorMessages.map((message) => (<Alert severity="error">{message}</Alert>))}
                </Box>
            )}
            {successMessages.length > 0 && (
                <Box sx={{flexBasis: '100%', mb: 2, mt: 2}}>
                    {successMessages.map((message) => (<Alert severity="success">{message}</Alert>))}
                </Box>
            )}
            {iframeData.showIframe && forQr && (<QRAmount amount={state.amount as number}/>)}
            {iframeData.showIframe && (
                <Box component={'iframe'} sx={{
                    border: 0,
                    background: 'transparent',
                    width: forQr ? '330px' : '520px',
                    paddingLeft: forQr ? '20px' : 0,
                    maxWidth: 1,
                    height: 1,
                    minHeight: forQr ? '320px' : '400px'
                }} src={iframeData.iframeUri}/>

            )}
            {iframeData.showIframe && forQr && (
                <QRTimer setIframeState={setIframeData} setConfirmationMessageData={setConfirmationMessageData}
                         paymentId={confirmationMessageData.paymentTxId}/>)}
            {!iframeData.showIframe && !confirmationMessageData.show && (
                <>
                    <UseQuerySuspense data={paymentMethods}>
                        {!forQr && (<><Typography variant={'body2'}
                                                  sx={{mb: 2, mt: 1}}>{t('chooseYourPaymentMethod')}</Typography>
                            <div
                                className={css`display: flex; flex-direction: row; gap: 8px; align-items:center; justify-content: space-between; max-width: 100%; flex-wrap: wrap;`}>
                                {paymentMethods.data?.paymentMethods?.map((value, key) => {
                                    if (value?.paymentProduct.name == 'Bancontact-QR') return (<></>); 
                                    else
                                        return (
                                            <PaymentMethodComponent key={key} fn={selectPaymenthMethod}
                                                                    currentMethod={state.selectedMethod}
                                                                    paymentMethod={value}/>);
                                })}</div>
                            <DepositLimits/>

                        </>)}
                        <Typography variant={'body2'} sx={{fontWeight: 600}}>{t('selectTheAmount')}</Typography>
                        <ButtonGroup variant="outlined" sx={{background: '#fff', justifyContent: 'space-between'}}>
                            <AmountButton amount={20} currentAmount={state.amount} fn={changeAmount}/>
                            <AmountButton amount={50} currentAmount={state.amount} fn={changeAmount}/>
                            <AmountButton amount={100} currentAmount={state.amount} fn={changeAmount}/>
                            <AmountButton amount={150} currentAmount={state.amount} fn={changeAmount}/>
                        </ButtonGroup>
                        <Divider sx={{alignItems: 'flex-start'}}>
                            <Typography variant={'body2'}>
                                {t('or')}
                            </Typography>
                        </Divider>
                        <TextInput
                            InputProps={{startAdornment: <InputAdornment position="start">€</InputAdornment>}}
                            sx={{width: 1}} placeholder={t('enterDifferentAmount')} value={state.amount}
                            onChange={(e) => {
                                let value: number | "" = parseInt(e.currentTarget.value);
                                if (isNaN(value as number) || value == 0)
                                    value = "";
                                setState({...state, amount: value});

                            }}></TextInput>
                        <LoadingButton loading={startPaymentState.loading}
                                       disabled={(state.amount != null && state.amount as number <= 0) || state.selectedMethod == null}
                                       variant={'contained'}
                                       onClick={onStartPayment}>{forQr ? t('generateMyQrCode') : t('deposit')}</LoadingButton>
                        {forQr && (<Button component={CustomLink} to={'/deposit'}>{t('otherDepositMethods')}</Button>)}
                    </UseQuerySuspense>
                    {forQr && (<DepositLimits/>)}
                    <OpenWithdrawals/>
                </>
            )}
            {!iframeData.showIframe && confirmationMessageData.show && (
                <UpdatePayment paymentId={confirmationMessageData.paymentTxId}/>
            )}
        </Stack>
    </Box>);
}